/**
 * TEMPORARY — one-time export of the API worker's encryption keys.
 * REMOVE AFTER USE (docs/security-review/KEY-EXPORT-RUNBOOK.md).
 *
 * Shows ENCRYPTION_KEY (and DB_CREDENTIALS_KEY when set) once, after the
 * super-admin re-enters their password and authenticator code. The values
 * live only in component state while the page is open: never in
 * localStorage, never logged, dropped on "Clear from screen" and on unmount.
 *
 * Also offers authenticator enrolment (the existing /admin/auth/mfa/setup and
 * /mfa/verify endpoints) when the signed-in super-admin has none yet, since
 * the export needs a code and the portal has no other enrolment screen.
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { KeyRound, ShieldAlert, ShieldCheck, Copy, Check, Eye, EyeOff, Lock, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const KEY_LABELS = {
  ENCRYPTION_KEY: 'ENCRYPTION_KEY (TFN / OAuth / integration secrets, AES-256-GCM)',
  DB_CREDENTIALS_KEY: 'DB_CREDENTIALS_KEY (firm database connection strings)',
};

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

const Notice = ({ tone = 'info', icon: Icon = AlertCircle, title, children }) => {
  const tones = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
  };
  return (
    <div className={`border rounded-lg p-4 flex items-start ${tones[tone]}`}>
      <Icon className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
      <div className="text-sm min-w-0">
        {title && <p className="font-semibold mb-1">{title}</p>}
        {children}
      </div>
    </div>
  );
};

const KeyRow = ({ name, value }) => {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const ok = await copyText(value);
    if (ok) {
      setCopied(true);
      toast.success(`${name} copied`);
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Copy failed. Select the value and copy it manually.');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <p className="label">{KEY_LABELS[name] || name}</p>
      {value ? (
        <div className="flex items-stretch gap-2">
          <input
            type={shown ? 'text' : 'password'}
            readOnly
            value={value}
            onFocus={(e) => e.target.select()}
            className="input font-mono text-xs bg-white"
            aria-label={`${name} value`}
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" onClick={() => setShown((s) => !s)} className="btn-secondary px-3" title={shown ? 'Hide' : 'Show'}>
            {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button type="button" onClick={onCopy} className="btn-primary px-3" title="Copy to clipboard">
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Not set on this worker (it falls back to ENCRYPTION_KEY).</p>
      )}
    </div>
  );
};

/** Authenticator enrolment for the signed-in admin: setup key, then verify a code. */
const EnrolAuthenticator = ({ email, onEnrolled }) => {
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await adminApi.mfaSetup();
      setSetup({ secret: r.data.secret, uri: r.data.otpAuthUri });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start authenticator setup');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from the app.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await adminApi.mfaVerify(code);
      toast.success('Authenticator enabled. You will be asked for a code at sign-in from now on.');
      setSetup(null);
      setCode('');
      onEnrolled();
    } catch (err) {
      setError(err.response?.data?.error || 'Code not accepted');
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (await copyText(setup.secret)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const grouped = setup ? setup.secret.replace(/(.{4})/g, '$1 ').trim() : '';

  return (
    <div className="space-y-4">
      <Notice tone="warning" icon={ShieldAlert} title="Authenticator app required">
        <p>
          Exporting keys needs your password and a code from an authenticator app, and MFA is not yet enabled on {email}.
          Enrol one here. Afterwards the admin portal will ask for the code each time you sign in.
        </p>
      </Notice>

      {error && (
        <Notice tone="danger">
          <p>{error}</p>
        </Notice>
      )}

      {!setup ? (
        <div className="card p-6">
          <button type="button" onClick={start} disabled={busy} className="btn-primary disabled:opacity-50">
            {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Set up authenticator
          </button>
        </div>
      ) : (
        <form onSubmit={verify} className="card p-6 space-y-4">
          <div>
            <p className="font-medium text-gray-900 mb-1">1. Add the account to your authenticator app</p>
            <p className="text-sm text-gray-600 mb-3">
              In Microsoft Authenticator or Google Authenticator choose <em>Add account → Other → Enter a setup key</em>.
              Account name: <span className="font-mono">{email}</span>. Key:
            </p>
            <div className="flex items-stretch gap-2">
              <input readOnly value={grouped} onFocus={(e) => e.target.select()} className="input font-mono tracking-wider bg-gray-50" aria-label="Authenticator setup key" />
              <button type="button" onClick={copySecret} className="btn-secondary px-3">
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Time-based, 6 digits. On a phone you can also open this link directly:{' '}
              <a href={setup.uri} className="text-primary-600 underline break-all">{setup.uri.split('?')[0]}</a>
            </p>
          </div>
          <div>
            <p className="font-medium text-gray-900 mb-1">2. Enter the code the app shows</p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="input tracking-widest w-40"
              placeholder="123456"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setSetup(null); setCode(''); }} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50">
              {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Verify and enable
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const KeyExportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Revealed values live only in this state; unmounting the page drops them.
  const [result, setResult] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await adminApi.getKeyExportStatus();
      setStatus(r.data);
    } catch (err) {
      const data = err.response?.data || {};
      setLoadError({
        message: data.error || 'Could not load key export status',
        detail: data.detail || (err.response ? `HTTP ${err.response.status}` : err.message),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || !/^\d{6}$/.test(totpCode)) {
      setError('Enter your password and the 6-digit authenticator code.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminApi.exportEncryptionKey({ password, totpCode });
      setResult({ keys: r.data.keys || {}, exportedAt: r.data.exportedAt });
      setStatus((s) => ({ ...(s || {}), exported: true, exportedAt: r.data.exportedAt, exportedBy: user?.email || null }));
      toast.success('Keys revealed. Copy them into your password manager now.');
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'already_exported') {
        setStatus((s) => ({ ...(s || {}), exported: true, exportedAt: data.exportedAt, exportedBy: data.exportedBy }));
      }
      setError([data.error || 'Key export failed', data.detail].filter(Boolean).join(' — '));
    } finally {
      setSubmitting(false);
      setPassword('');
      setTotpCode('');
    }
  };

  const clearScreen = () => {
    setResult(null);
    toast('Cleared from screen');
  };

  const when = (iso) => {
    try {
      return format(new Date(iso), 'd MMM yyyy, h:mm a');
    } catch {
      return String(iso || '');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <KeyRound className="w-6 h-6 mr-2 text-primary-600" />
            Encryption Key Export
          </h1>
          <p className="text-gray-500 mt-1">
            One-time reveal of the API worker&apos;s encryption keys for escrow in your password manager.
          </p>
        </div>
        <span className="badge-danger">Temporary · one time</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : loadError ? (
        <Notice tone="danger" title="Status unavailable">
          <p>{loadError.message}</p>
          {loadError.detail && <p className="font-mono text-xs mt-1 break-all">{loadError.detail}</p>}
          <button type="button" onClick={loadStatus} className="btn-secondary mt-3">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </button>
        </Notice>
      ) : !status?.enabled ? (
        <Notice tone="info" icon={Lock} title="Turned off">
          <p>
            Key export is disabled on the API worker. To enable it, add a Text variable{' '}
            <span className="font-mono">ALLOW_KEY_EXPORT</span> = <span className="font-mono">true</span> to the
            practice-manager-api worker in the Cloudflare dashboard (Settings → Variables and Secrets) and deploy,
            then come back here. Remove it again once the keys are stored.
          </p>
        </Notice>
      ) : result ? (
        <div className="space-y-4">
          <Notice tone="warning" icon={ShieldAlert} title="Shown once. Copy both values into your password manager now.">
            <p>
              Leaving this page clears them, and the export cannot be repeated. Nothing has been written to logs; the audit log
              records only that the export happened.
            </p>
          </Notice>
          <div className="card p-6 space-y-4">
            {Object.keys(result.keys).map((name) => (
              <KeyRow key={name} name={name} value={result.keys[name]} />
            ))}
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-500">Exported {when(result.exportedAt)}</p>
              <button type="button" onClick={clearScreen} className="btn-danger">
                Clear from screen
              </button>
            </div>
          </div>
          <Notice tone="info" title="Afterwards">
            <p>Remove the <span className="font-mono">ALLOW_KEY_EXPORT</span> variable in Cloudflare and delete this feature (see the runbook in docs/security-review).</p>
          </Notice>
        </div>
      ) : status.exported ? (
        <Notice tone="success" icon={CheckCircle} title="Already exported">
          <p>
            The keys were exported {status.exportedAt ? `on ${when(status.exportedAt)}` : 'previously'}
            {status.exportedBy ? ` by ${status.exportedBy}` : ''}. This action only runs once and cannot be repeated.
            Remove <span className="font-mono">ALLOW_KEY_EXPORT</span> and delete the feature.
          </p>
        </Notice>
      ) : !status.isSuperAdmin ? (
        <Notice tone="danger" icon={Lock} title="Super-admin only">
          <p>Your admin account ({user?.email}) does not have the super_admin role, so it cannot export keys.</p>
        </Notice>
      ) : !status.mfaEnrolled ? (
        <EnrolAuthenticator email={user?.email} onEnrolled={loadStatus} />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Notice tone="warning" icon={ShieldAlert} title="This can only be done once">
            <p>
              You will see {status.keys?.DB_CREDENTIALS_KEY ? 'ENCRYPTION_KEY and DB_CREDENTIALS_KEY' : 'ENCRYPTION_KEY'} one time.
              The export is recorded in the admin audit log (without the value) and any later attempt is refused.
              Have your password manager open before you continue.
            </p>
          </Notice>

          {error && (
            <Notice tone="danger">
              <p>{error}</p>
            </Notice>
          )}

          <div className="card p-6 space-y-4">
            <div>
              <label htmlFor="ke-password" className="label">Your admin password</label>
              <input
                id="ke-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="ke-totp" className="label">Authenticator code</label>
              <input
                id="ke-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                className="input tracking-widest w-40"
                placeholder="123456"
              />
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={submitting} className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                {submitting ? 'Verifying…' : 'Reveal keys (one time)'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default KeyExportPage;
